<template>
  <div class="gs-mux-upload">
    <input
      ref="file"
      data-field="mux-file"
      type="file"
      accept="video/*"
      class="gs-mux-upload__input"
      tabindex="-1"
      aria-hidden="true"
      :disabled="disabled || busy"
      @change="onFileInput"
    />
    <button
      v-if="!file"
      type="button"
      data-action="choose-file"
      class="gs-mux-upload__drop"
      :class="{ 'is-dragging': dragging }"
      :disabled="disabled"
      @click="$refs.file.click()"
      @dragover.prevent="dragging = !disabled"
      @dragleave.prevent="dragging = false"
      @drop.prevent="onDrop"
    >
      <span class="gs-mux-upload__drop-icon" aria-hidden="true"><gs-icon name="play" size="20" /></span>
      <span class="gs-mux-upload__drop-title">{{ dragging ? 'Drop your video to add it' : 'Choose a video file' }}</span>
      <span class="gs-mux-upload__drop-hint">Or drag it here. MP4, MOV and WebM work. The file goes straight to your Mux account.</span>
    </button>
    <div v-else class="gs-mux-upload__file" :class="{ 'is-done': phase === 'done' }">
      <div class="gs-mux-upload__file-row">
        <span class="gs-mux-upload__file-icon" aria-hidden="true">
          <gs-icon :name="phase === 'done' ? 'check' : 'play'" size="16" />
        </span>
        <span class="gs-mux-upload__file-text">
          <span class="gs-mux-upload__file-name" data-file-name :title="file.name">{{ file.name }}</span>
          <span class="gs-mux-upload__file-size">{{ sizeLabel }}</span>
        </span>
        <gs-button
          v-if="!busy && phase !== 'done'"
          variant="ghost"
          size="sm"
          data-action="change-file"
          :disabled="disabled"
          @click="clear()"
        >
          Change
        </gs-button>
      </div>
      <div
        v-if="busy || phase === 'done'"
        class="gs-mux-upload__track"
        role="progressbar"
        aria-label="Upload progress"
        aria-valuemin="0"
        aria-valuemax="100"
        :aria-valuenow="progress"
      >
        <div class="gs-mux-upload__bar" :class="{ 'is-paused': paused }" :style="{ width: progress + '%' }"></div>
      </div>
      <p v-if="busy" class="gs-mux-upload__status" :class="{ 'is-paused': paused }" data-upload-status role="status">{{ statusText }}</p>
      <p v-else-if="phase === 'done'" class="gs-mux-upload__status" data-upload-status role="status">Uploaded. Mux is preparing your video.</p>
    </div>
    <p v-if="error" class="gs-mux-upload__error" role="alert">{{ error }}</p>
  </div>
</template>

<script>
import { startMuxUpload } from '@/utils/muxUpload';

export function formatBytes(bytes) {
  const value = Number(bytes) || 0;
  if (value >= 1024 * 1024 * 1024) return `${(value / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  if (value >= 1024 * 1024) return `${(value / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(value / 1024))} KB`;
}

export default {
  name: 'MuxUploadPanel',
  props: {
    disabled: { type: Boolean, default: false },
    uploader: { type: Function, default: startMuxUpload },
  },
  data() {
    return { file: null, phase: 'idle', progress: 0, paused: false, error: '', dragging: false, session: null };
  },
  computed: {
    busy() {
      return this.phase === 'uploading';
    },
    sizeLabel() {
      if (!this.file) return '';
      const total = formatBytes(this.file.size);
      if (!this.busy) return total;
      return `${formatBytes((this.file.size * this.progress) / 100)} of ${total}`;
    },
    statusText() {
      return this.paused ? 'Paused. Waiting for your connection to come back…' : `Uploading… ${this.progress}%`;
    },
  },
  watch: {
    // Leaving the page mid-upload would abandon it, so ask the browser to confirm first.
    busy(isBusy) {
      if (isBusy) window.addEventListener('beforeunload', this.warnBeforeLeaving);
      else window.removeEventListener('beforeunload', this.warnBeforeLeaving);
    },
  },
  beforeDestroy() {
    window.removeEventListener('beforeunload', this.warnBeforeLeaving);
    this.abort();
  },
  methods: {
    warnBeforeLeaving(event) {
      event.preventDefault();
      // eslint-disable-next-line no-param-reassign
      event.returnValue = '';
    },
    onFileInput(event) {
      this.choose(event.target.files && event.target.files[0]);
    },
    onDrop(event) {
      this.dragging = false;
      this.choose(event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files[0]);
    },
    choose(file) {
      if (!file || this.busy || this.disabled) return;
      if (file.type && file.type.indexOf('video/') !== 0) {
        this.error = "That file isn't a video. Choose an MP4, MOV, WebM or another video file.";
        return;
      }
      this.file = file;
      this.error = '';
      this.progress = 0;
      this.paused = false;
      this.phase = 'ready';
      this.$emit('file-chosen', file);
    },
    clear() {
      if (this.busy) return;
      this.file = null;
      this.error = '';
      this.progress = 0;
      this.phase = 'idle';
      if (this.$refs.file) this.$refs.file.value = '';
      this.$emit('cleared');
    },
    start(uploadUrl) {
      if (!this.file) return Promise.reject(new Error('Choose a video file first.'));
      if (this.session) this.abort();
      this.error = '';
      this.paused = false;
      this.progress = 0;
      this.phase = 'uploading';
      let session = null;
      // Ignore late events from a session that was aborted or replaced.
      const current = () => session !== null && this.session === session;
      try {
        session = this.uploader({
          endpoint: uploadUrl,
          file: this.file,
          onProgress: value => {
            if (current()) this.progress = value;
          },
          onPaused: () => {
            if (current()) this.paused = true;
          },
          onResumed: () => {
            if (current()) this.paused = false;
          },
        });
      } catch (error) {
        this.phase = 'error';
        this.error = (error && error.message) || 'The upload failed. Try again.';
        return Promise.reject(error);
      }
      this.session = session;
      return session.done.then(
        () => {
          if (!current()) return;
          this.session = null;
          this.paused = false;
          this.progress = 100;
          this.phase = 'done';
        },
        error => {
          if (current()) {
            this.session = null;
            this.paused = false;
            this.phase = 'error';
            this.error = (error && error.message) || 'The upload failed. Try again.';
          }
          throw error;
        }
      );
    },
    abort() {
      if (this.session) {
        const session = this.session;
        this.session = null;
        session.abort();
      }
      if (this.phase === 'uploading') {
        this.phase = this.file ? 'ready' : 'idle';
        this.paused = false;
        this.progress = 0;
      }
    },
  },
};
</script>

<style scoped>
.gs-mux-upload {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.gs-mux-upload__input {
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
  pointer-events: none;
}

.gs-mux-upload__drop {
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 160px;
  padding: 24px;
  color: var(--gs-foreground);
  background: var(--gs-surface-subtle);
  border: 1.5px dashed var(--gs-border-strong);
  border-radius: var(--gs-radius-lg);
  font: inherit;
  text-align: center;
  cursor: pointer;
  transition: border-color 150ms ease, background-color 150ms ease;
}

/* Children must not steal drag events, or dragleave fires while still over the drop zone. */
.gs-mux-upload__drop > * {
  pointer-events: none;
}

.gs-mux-upload__drop:hover:not(:disabled),
.gs-mux-upload__drop.is-dragging {
  background: var(--gs-active-soft);
  border-color: var(--gs-active);
}

.gs-mux-upload__drop:focus-visible {
  outline: 2px solid var(--gs-active);
  outline-offset: 2px;
}

.gs-mux-upload__drop:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.gs-mux-upload__drop-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  margin-bottom: 4px;
  color: var(--gs-muted-foreground);
  background: var(--gs-muted);
  border-radius: 999px;
  transition: color 150ms ease, background-color 150ms ease;
}

.gs-mux-upload__drop:hover:not(:disabled) .gs-mux-upload__drop-icon,
.gs-mux-upload__drop.is-dragging .gs-mux-upload__drop-icon {
  color: var(--gs-active);
  background: var(--gs-surface);
}

.gs-mux-upload__drop-title {
  font-size: 14px;
  font-weight: 600;
}

.gs-mux-upload__drop-hint {
  max-width: 48ch;
  color: var(--gs-muted-foreground);
  font-size: 13px;
  line-height: 1.5;
}

.gs-mux-upload__file {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 14px 16px;
  background: var(--gs-surface);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-md);
}

.gs-mux-upload__file-row {
  display: flex;
  gap: 12px;
  align-items: center;
  min-width: 0;
}

.gs-mux-upload__file-icon {
  display: inline-flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  color: var(--gs-muted-foreground);
  background: var(--gs-muted);
  border-radius: var(--gs-radius-sm);
}

.gs-mux-upload__file.is-done .gs-mux-upload__file-icon {
  color: var(--gs-success);
  background: var(--gs-success-soft);
}

.gs-mux-upload__file-text {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.gs-mux-upload__file-name {
  overflow: hidden;
  color: var(--gs-foreground);
  font-size: 14px;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.gs-mux-upload__file-size {
  color: var(--gs-muted-foreground);
  font-size: 12px;
  font-variant-numeric: tabular-nums;
}

.gs-mux-upload__track {
  height: 6px;
  overflow: hidden;
  background: var(--gs-muted);
  border-radius: 999px;
}

.gs-mux-upload__bar {
  height: 100%;
  background: var(--gs-active);
  border-radius: inherit;
  transition: width 200ms ease, background-color 150ms ease;
}

.gs-mux-upload__bar.is-paused {
  background: var(--gs-warning);
}

.gs-mux-upload__file.is-done .gs-mux-upload__bar {
  background: var(--gs-success);
}

.gs-mux-upload__status {
  margin: 0;
  color: var(--gs-muted-foreground);
  font-size: 13px;
  font-variant-numeric: tabular-nums;
}

.gs-mux-upload__status.is-paused {
  color: var(--gs-warning);
}

.gs-mux-upload__error {
  margin: 0;
  padding: 10px 12px;
  color: var(--gs-danger);
  background: var(--gs-surface-subtle);
  border: 1px solid var(--gs-danger-border);
  border-radius: var(--gs-radius-sm);
  font-size: 13px;
  line-height: 1.5;
}
</style>
